import type { ReactNode } from "react";

import PrivacyTipOutlined from "@mui/icons-material/PrivacyTipOutlined";
import VerticalSplitOutlined from "@mui/icons-material/VerticalSplitOutlined";
import {
  Box,
  Drawer,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { StatusChip } from "components/StatusChip";

import {
  SCOUT_INFO_PANEL_WIDTH_PX,
  SCOUT_KNOWLEDGE_BASE_URL,
  SCOUT_PRIVACY_CONTENT,
  SCOUT_SOURCES_CONTENT,
  type ScoutInfoPanelItem,
  type ScoutInfoPanelTab,
} from "./scoutInfoPanelContent";

const PANEL_HEADER_HEIGHT_SPACING = 7;

const SCOUT_INFO_PANEL_TABS: { value: ScoutInfoPanelTab; label: string }[] = [
  { value: "sources", label: "Sources" },
  { value: "privacy", label: "Privacy & Data" },
];

type ScoutInfoPanelVariant = "persistent" | "temporary";

type ScoutInfoPanelProps = {
  tab: ScoutInfoPanelTab;
  onTabChange: (tab: ScoutInfoPanelTab) => void;
  onClose: () => void;
  variant: ScoutInfoPanelVariant;
  open?: boolean;
};

const drawerPaperSx = {
  width: SCOUT_INFO_PANEL_WIDTH_PX,
  boxSizing: "border-box",
  bgcolor: "background.paper",
} as const;

export function ScoutInfoPanel({
  tab,
  onTabChange,
  onClose,
  variant,
  open = false,
}: ScoutInfoPanelProps) {
  const body = (
    <ScoutInfoPanelBody
      tab={tab}
      onTabChange={onTabChange}
      onClose={onClose}
      closeLabel={
        variant === "persistent"
          ? "Hide sources and privacy panel"
          : "Close sources and privacy panel"
      }
      closeIcon={<VerticalSplitOutlined fontSize="small" />}
      headerHeightSpacing={variant === "persistent" ? PANEL_HEADER_HEIGHT_SPACING : undefined}
    />
  );

  if (variant === "persistent") {
    return (
      <Drawer
        variant="persistent"
        anchor="right"
        open={open}
        sx={{
          width: open ? SCOUT_INFO_PANEL_WIDTH_PX : 0,
          flexShrink: 0,
          position: "absolute",
          right: 0,
          top: 0,
          height: "100%",
          "& .MuiDrawer-paper": {
            ...drawerPaperSx,
            position: "absolute",
            top: 0,
            right: 0,
            height: "100%",
            borderLeft: 1,
            borderColor: "divider",
          },
        }}
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      disableScrollLock
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": {
          ...drawerPaperSx,
          width: "100%",
        },
      }}
    >
      {body}
    </Drawer>
  );
}

function ScoutInfoPanelBody({
  tab,
  onTabChange,
  onClose,
  closeLabel,
  closeIcon,
  headerHeightSpacing,
}: {
  tab: ScoutInfoPanelTab;
  onTabChange: (tab: ScoutInfoPanelTab) => void;
  onClose: () => void;
  closeLabel: string;
  closeIcon: ReactNode;
  headerHeightSpacing?: number;
}) {
  const content = tab === "sources" ? SCOUT_SOURCES_CONTENT : SCOUT_PRIVACY_CONTENT;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: headerHeightSpacing ? (theme) => theme.spacing(headerHeightSpacing) : 48,
          px: isMobile ? 3 : 2,
          py: isMobile ? 2 : 1,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1} role="tablist" aria-label="Scout sources and privacy">
          {SCOUT_INFO_PANEL_TABS.map(({ value, label }) => {
            const selected = tab === value;

            return (
              <StatusChip
                key={label}
                label={label}
                color={selected ? "info" : "default"}
                variant="outlined"
                size={isMobile ? "medium" : "small"}
                onClick={() => onTabChange(value)}
                role="tab"
                aria-selected={selected}
              />
            );
          })}
        </Stack>
        <Tooltip title={closeLabel}>
          <IconButton
            size={isMobile ? "medium" : "small"}
            onClick={onClose}
            aria-label={closeLabel}
          >
            {closeIcon}
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: isMobile ? 3 : 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Stack spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {content.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
            {content.subtitle}
          </Typography>

          <Stack spacing={3} pt={3}>
            {content.items.map((item) => (
              <ScoutInfoPanelListItem key={item.title} item={item} />
            ))}
          </Stack>
        </Stack>

        {tab === "privacy" ? (
          <Link
            href={SCOUT_KNOWLEDGE_BASE_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
              fontWeight: 400,
              fontSize: 14,
              gap: 1,
              mt: 3,
            }}
          >
            <PrivacyTipOutlined sx={{ fontSize: 20 }} />
            Learn more about our Knowledge Base
          </Link>
        ) : null}
      </Box>
    </Box>
  );
}

function ScoutInfoPanelListItem({ item }: { item: ScoutInfoPanelItem }) {
  return (
    <Stack direction="column" spacing={2} alignItems="flex-start">
      <Box
        component="img"
        src={item.iconSrc}
        alt={item.title}
        sx={{
          width: 56,
          height: 56,
        }}
      />
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1">{item.title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {item.description}
        </Typography>
      </Stack>
    </Stack>
  );
}
